import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Popover,
  PopoverActions,
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Candidate, getCandidateLabel } from "../../services/preventiviApi";
import { searcherApi, BomItemOut } from "../../services/searcherApi";
import { CandidateCard } from "./CandidateCard";
import { CandidateDetailsModal } from "./CandidateDetailsModal";
import toast from "react-hot-toast";

const PAGE_SIZE = 5;
const POPOVER_WIDTH = 480;
const POPOVER_MAX_H = 560;
const VIEWPORT_PAD = 12;
const MANUAL_SEARCH_LIMIT = 20;

/** Catalog keyword hit → Candidate shape used by CandidateCard. */
function bomItemToCandidate(item: BomItemOut): Candidate {
  return {
    uid: item.uid,
    final_score: 1,
    rerank_score: 1,
    match_type: "campione",
    match_percent: null,
    reasoning: "",
    text: item.descrizione_breve ?? item.title,
    metadata: {
      title: item.title,
      description: item.description,
      descrizione_breve: item.descrizione_breve ?? undefined,
      specs: item.specs,
      codice: item.codice ?? undefined,
      prezzo: item.prezzo ?? undefined,
      costo_mat_unitario: item.costo_mat_unitario ?? undefined,
      tempo_posa_ore: item.tempo_posa_ore ?? undefined,
      costo_mano_unitario: item.costo_mano_unitario ?? undefined,
      categoria: item.categoria ?? undefined,
      disciplina: item.disciplina ?? undefined,
      source_file: item.source_file ?? undefined,
      anno: item.anno ?? undefined,
      udm: item.udm ?? undefined,
    },
  };
}

interface MatchPickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  queryText: string;
  suggestions: Candidate[];
  loadingSuggestions: boolean;
  selectedUid?: string | null;
  onChoose: (candidate: Candidate) => void;
  /** Called when the user removes the current selection without replacing it. */
  onDeselect?: () => void;
  preventivoCategoria?: string | null;
  disciplina?: "ELE" | "MEC";
}

/**
 * The popover always gets the full comfortable height (capped by the viewport),
 * never just the gap between the row and the screen edge — that gap is tiny
 * for rows in the middle of the screen and made the content unreadable.
 * It opens on a side where it fits entirely; when neither side is tall enough
 * it opens on the roomier one and MUI shifts it back inside the viewport
 * (marginThreshold), overlapping the row instead of being squashed or clipped.
 */
function usePopoverPlacement(anchorEl: HTMLElement | null) {
  return useMemo(() => {
    if (!anchorEl || typeof window === "undefined") {
      return { openAbove: false, maxHeight: POPOVER_MAX_H };
    }
    const rect = anchorEl.getBoundingClientRect();
    const maxHeight = Math.max(0, Math.min(POPOVER_MAX_H, window.innerHeight - VIEWPORT_PAD * 2));
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
    const spaceAbove = rect.top - VIEWPORT_PAD;
    const openAbove = spaceBelow >= maxHeight ? false : spaceAbove >= maxHeight ? true : spaceAbove > spaceBelow;
    return { openAbove, maxHeight };
  }, [anchorEl]);
}

/** Re-runs MUI's viewport clamping whenever the paper changes size (tab switch, results, expand). */
function useRepositionOnResize(open: boolean) {
  const actionRef = useRef<PopoverActions | null>(null);
  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open || !paperEl || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => actionRef.current?.updatePosition());
    ro.observe(paperEl);
    return () => ro.disconnect();
  }, [open, paperEl]);
  return { actionRef, paperRef: setPaperEl };
}

export const MatchPickerPopover: React.FC<MatchPickerPopoverProps> = ({
  anchorEl,
  onClose,
  queryText,
  suggestions,
  loadingSuggestions,
  selectedUid,
  onChoose,
  onDeselect,
  preventivoCategoria,
}) => {
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [detailCandidate, setDetailCandidate] = useState<Candidate | null>(null);
  const [showCrossCategory, setShowCrossCategory] = useState(false);
  const [sameCatPage, setSameCatPage] = useState(1);
  const [crossCatPage, setCrossCatPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const { openAbove, maxHeight } = usePopoverPlacement(anchorEl);
  const { actionRef, paperRef } = useRepositionOnResize(Boolean(anchorEl));

  // Ricerca manuale = keyword lookup sul catalogo DB (ILIKE), non il pipeline
  // semantico+LLM usato per i suggerimenti automatici. Altrimenti query corte
  // tipo "Contatore" finiscono filtrate/vuote senza un errore chiaro.
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    setSearchPage(1);
    setHasSearched(true);
    setSearchError(null);
    setSearchResults([]);
    try {
      const res = await searcherApi.listCatalog({
        q,
        limit: MANUAL_SEARCH_LIMIT,
        offset: 0,
        // No disciplina hard-filter: manual browse must surface catalog hits
        // even if the row's disciplina differs.
      });
      setSearchResults(res.items.map(bomItemToCandidate));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Errore nella ricerca catalogo";
      setSearchError(msg);
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const handleChoose = (c: Candidate) => {
    onChoose(c);
    onClose();
  };

  const handleDeselect = () => {
    onDeselect?.();
    onClose();
  };

  // A candidate is "manual" if it carries the new flag or the legacy match_type value.
  const isManual = (c: Candidate) => c.manually_selected === true || c.match_type === "manual";

  // Show pipeline-positive candidates plus any manually-selected one (both new
  // flag and legacy match_type=manual).
  // Sort: best pipeline type first (codice → exact → acceptable), then by score.
  const TYPE_RANK: Record<string, number> = { codice: 0, exact: 1, acceptable: 2 };
  const visible = suggestions
    .filter((c) =>
      c.uid === selectedUid ||
      isManual(c) ||
      ["codice", "exact", "acceptable"].includes(c.match_type ?? "")
    )
    .sort((a, b) => {
      const ra = TYPE_RANK[a.match_type ?? ""] ?? 9;
      const rb = TYPE_RANK[b.match_type ?? ""] ?? 9;
      if (ra !== rb) return ra - rb;
      return (b.match_percent ?? 0) - (a.match_percent ?? 0);
    });

  // For display inside the popover list the "Manuale" label should appear ONLY
  // on the currently selected item. Non-selected items must show their original
  // pipeline match_type. For legacy data where match_type was permanently
  // overwritten to "manual" we cannot recover the original; in that case we
  // suppress the chip entirely (null) rather than show a misleading label.
  const forDisplay = (c: Candidate): Candidate => {
    if (!isManual(c) || c.uid === selectedUid) return c;
    const restoredType = c.match_type === "manual" ? null : c.match_type;
    return { ...c, manually_selected: false, match_type: restoredType };
  };

  const sameCategory = preventivoCategoria
    ? visible.filter((c) => (c.metadata?.categoria ?? "") === preventivoCategoria)
    : visible;
  const crossCategory = preventivoCategoria
    ? visible.filter((c) => (c.metadata?.categoria ?? "") !== preventivoCategoria)
    : [];

  const displayedSame = sameCategory.slice(0, PAGE_SIZE * sameCatPage);
  const hasMoreSame = displayedSame.length < sameCategory.length;

  const displayedCross = crossCategory.slice(0, PAGE_SIZE * crossCatPage);
  const hasMoreCross = displayedCross.length < crossCategory.length;
  const hasCross = crossCategory.length > 0;

  // Header: extended description of the current match, or of the first proposed
  // suggestion when nothing is selected. The row's own text is already visible
  // in the table, so it is only used when there is no candidate at all.
  const selectedCandidate = selectedUid
    ? visible.find((c) => c.uid === selectedUid && (isManual(c) || (c.match_type ?? "") in TYPE_RANK)) ?? null
    : null;
  const headerCandidate = selectedCandidate ?? (preventivoCategoria ? sameCategory[0] ?? crossCategory[0] : visible[0]) ?? null;
  const headerMeta = headerCandidate?.metadata ?? {};
  const headerText = headerCandidate
    ? String(headerMeta.description || headerMeta.descrizione_breve || getCandidateLabel(headerCandidate))
    : queryText;
  const headerLabel = selectedCandidate
    ? "Descrizione estesa · match selezionato"
    : headerCandidate
      ? "Descrizione estesa · primo suggerimento"
      : "Voce del preventivo";

  const displayedSearch = searchResults.slice(0, PAGE_SIZE * searchPage);
  const hasMoreSearch = displayedSearch.length < searchResults.length;

  const altraCatBadge = (
    <Chip
      label="altra cat."
      size="small"
      sx={{
        height: 16,
        fontSize: "0.6rem",
        bgcolor: "#FFF3E0",
        color: "#E65100",
      }}
    />
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      action={actionRef}
      anchorOrigin={{ vertical: openAbove ? "top" : "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: openAbove ? "bottom" : "top", horizontal: "left" }}
      marginThreshold={VIEWPORT_PAD}
      slotProps={{
        paper: {
          ref: paperRef,
          sx: {
            width: POPOVER_WIDTH,
            maxWidth: `calc(100vw - ${VIEWPORT_PAD * 2}px)`,
            maxHeight,
            borderRadius: "14px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1, gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, minWidth: 0 }}>
              <Typography
                variant="caption"
                noWrap
                sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.disabled", fontSize: "0.64rem" }}
              >
                {headerLabel}
              </Typography>
              {headerMeta.codice && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: "0.68rem", flexShrink: 0 }}
                >
                  {headerMeta.codice}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                maxHeight: 132,
                overflowY: "auto",
                overscrollBehavior: "contain",
                pr: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.45, whiteSpace: "pre-wrap", fontSize: "0.8rem" }}
              >
                {headerText}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, flexShrink: 0, mt: -0.5 }}>
            <Tooltip title="Chiudi" placement="left">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {selectedUid && onDeselect && (
              <Tooltip title="Rimuovi il match attuale (la riga resta senza match)" placement="left">
                <IconButton
                  size="small"
                  onClick={handleDeselect}
                  aria-label="Rimuovi il match attuale"
                  sx={{
                    p: 0.5,
                    color: "error.main",
                    border: "1px solid",
                    borderColor: "rgba(211,47,47,0.35)",
                    bgcolor: "rgba(211,47,47,0.04)",
                    "&:hover": { bgcolor: "rgba(211,47,47,0.12)", borderColor: "error.main" },
                  }}
                >
                  <LinkOffIcon sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSameCatPage(1); setCrossCatPage(1); setSearchPage(1); }} sx={{ minHeight: 36 }}>
          <Tab label="Suggerimenti" sx={{ fontSize: "0.82rem", minHeight: 36, py: 0 }} />
          <Tab label="Ricerca manuale" sx={{ fontSize: "0.82rem", minHeight: 36, py: 0 }} />
        </Tabs>
      </Box>

      <CandidateDetailsModal
        candidate={detailCandidate}
        open={Boolean(detailCandidate)}
        onClose={() => setDetailCandidate(null)}
        queryText={queryText}
      />

      {/* Content — always scrollable within available viewport height */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          p: 2,
        }}
      >
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {loadingSuggestions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : visible.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                Nessun suggerimento trovato
              </Typography>
            ) : (
              <>
                {/* Same-category section */}
                {preventivoCategoria && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Stessa categoria ({preventivoCategoria})
                    </Typography>
                    <Chip label={String(sameCategory.length)} size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
                  </Box>
                )}
                {(preventivoCategoria ? displayedSame : visible).map((c) => (
                  <CandidateCard
                    key={c.uid}
                    candidate={forDisplay(c)}
                    selected={c.uid === selectedUid}
                    onChoose={() => handleChoose(c)}
                    onDeselect={c.uid === selectedUid ? handleDeselect : undefined}
                    onDetails={() => setDetailCandidate(c)}
                    compact
                  />
                ))}
                {preventivoCategoria && hasMoreSame && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSameCatPage((p) => p + 1)}
                    sx={{ alignSelf: "center", fontSize: "0.78rem" }}
                  >
                    Mostra altri ({sameCategory.length - displayedSame.length})
                  </Button>
                )}

                {/* Cross-category section */}
                {hasCross && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                      onClick={() => setShowCrossCategory(!showCrossCategory)}
                    >
                      <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Altre categorie
                      </Typography>
                      <Chip label={String(crossCategory.length)} size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
                      <ExpandMoreIcon
                        sx={{
                          fontSize: "1rem",
                          color: "text.disabled",
                          transform: showCrossCategory ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                        }}
                      />
                    </Box>
                    {showCrossCategory && (
                      <>
                        {displayedCross.map((c) => (
                          <CandidateCard
                            key={c.uid}
                            candidate={forDisplay(c)}
                            selected={c.uid === selectedUid}
                            onChoose={() => handleChoose(c)}
                            onDeselect={c.uid === selectedUid ? handleDeselect : undefined}
                            onDetails={() => setDetailCandidate(c)}
                            compact
                            badge={altraCatBadge}
                          />
                        ))}
                        {hasMoreCross && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setCrossCatPage((p) => p + 1)}
                            sx={{ alignSelf: "center", fontSize: "0.78rem" }}
                          >
                            Mostra altri ({crossCategory.length - displayedCross.length})
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca nel catalogo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: "1rem", color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px" },
                }}
              />
              <IconButton
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px" }}
              >
                {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {searching && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {!searching && searchError && (
                <Typography variant="body2" color="error" sx={{ textAlign: "center", py: 2 }}>
                  {searchError}
                </Typography>
              )}
              {!searching && !searchError && displayedSearch.map((c) => (
                <CandidateCard
                  key={c.uid}
                  candidate={c}
                  selected={c.uid === selectedUid}
                  onChoose={() => handleChoose(c)}
                  onDeselect={c.uid === selectedUid ? handleDeselect : undefined}
                  onDetails={() => setDetailCandidate(c)}
                  compact
                />
              ))}
              {!searching && !searchError && hasMoreSearch && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSearchPage((p) => p + 1)}
                  sx={{ alignSelf: "center", fontSize: "0.78rem" }}
                >
                  Mostra altri ({searchResults.length - displayedSearch.length})
                </Button>
              )}
              {!searching && !searchError && hasSearched && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                  Nessun risultato — prova una query diversa
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Popover>
  );
};
